import { CreateCdfParams } from 'features/dataLayers'
import { getTMinMethod } from 'features/fde/tminMethod/tminMethodCallback'
import {
  AssetHealthDataFrame,
  AssetHealthDataLayers,
  ForecastComponentConfig,
  GeckoAssetModel,
  GeckoComponentDataFrame,
  GeckoMeasurementUnit,
  GeckoSource,
  GeckoView,
} from 'types'

const selectedDataLayerToCdfArgsMap = {
  thickness: {
    id: 'custom-cdfLoad-thickness-scenario',
    source: GeckoSource.Projection,
    view: GeckoView.Thickness,
    measurementUnit: GeckoMeasurementUnit.Inches,
  },
  lpy: {
    id: 'custom-cdfLoad-lpy-scenario',
    source: GeckoSource.Projection,
    view: GeckoView.LongTermCorrosionRate,
    measurementUnit: GeckoMeasurementUnit.Inches,
  },
  tmin: {
    id: 'custom-cdfLoad-tmin-scenario',
    source: GeckoSource.Projection,
    view: GeckoView.Thickness,
    measurementUnit: GeckoMeasurementUnit.Inches,
  },
  rl: {
    id: 'custom-cdfLoad-rl-scenario',
    source: GeckoSource.Projection,
    view: GeckoView.RemainingLife,
    measurementUnit: GeckoMeasurementUnit.Years,
  },
}

export const ahpDataFrameCDFIds = Object.values(
  selectedDataLayerToCdfArgsMap
).map((args) => args.id)

/**
 * Generate CDF Args from an Asset Health Plan DataFrame
 * @param relevantThicknessCDF - The backing thickness CDF for the asset, sourced from the details page context
 * @param selectedDataFrame - The selected dataframe from the asset health plan
 * @param selectedComponentModel TODO: Document
 * @param forecastComponentConfig TODO: Document
 * @param selectedDataLayer - The selected data layer from the asset health plan, Ex) rl = Remaining Life
 * @param generation - Optional, a unique number to denote the version of the cdf. Used when the custom cdf is recalculated to ensure the data is updated in the app.
 */
export function customCDFArgsFromAHPDataframes({
  relevantThicknessCDF,
  selectedDataFrame,
  assetModel,
  componentId,
  forecastComponentConfig,
  selectedDataLayer,
}: {
  relevantThicknessCDF?: GeckoComponentDataFrame
  selectedDataFrame?: AssetHealthDataFrame
  assetModel: GeckoAssetModel | undefined
  componentId: string
  forecastComponentConfig: ForecastComponentConfig | undefined
  selectedDataLayer: string
}): (CreateCdfParams & { id: string }) | null {
  if (!relevantThicknessCDF || !selectedDataFrame || !assetModel) {
    return null
  }

  const tminMethod = getTMinMethod(
    assetModel,
    componentId,
    relevantThicknessCDF,
    forecastComponentConfig
  )

  return {
    aggregation: relevantThicknessCDF.data.params.aggregation,
    anglePlotSetting: relevantThicknessCDF.data.params.angle_plot_setting,
    algorithm: relevantThicknessCDF.data.params.algorithm,
    tminMethod,
    binning: relevantThicknessCDF.data.binning,
    geometry: relevantThicknessCDF.data.geometry,
    tags: relevantThicknessCDF.tags,
    data: selectedDataFrame[selectedDataLayer as keyof AssetHealthDataLayers],
    ...selectedDataLayerToCdfArgsMap[
      selectedDataLayer as keyof AssetHealthDataLayers
    ],
  }
}
